import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { VehicleMakesService } from '../../service/vehicle-makes.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CreateMakeResponse, VehicleModelItem } from '../../api/vehicles';
import { AvatarModule } from 'primeng/avatar';
import { PanelModule } from 'primeng/panel';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { AccordionModule } from 'primeng/accordion';
import { ActivatedRoute, Router } from '@angular/router';
import { ProgressLoaderService } from '../../service/progress-loader.service';
import { BadgeModule } from 'primeng/badge';
import { VehicleModelsService } from '../../service/vehicle-models.service';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, Message, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { NgxTranslateModule } from 'src/app/translate.module';
import { LanguageService } from '../../service/language.service';
import { languages } from '../../constants/languages';
import { showErrorToast } from '../../utilities/toast-utils';

@Component({
    selector: 'app-vehicle-makes',
    standalone: true,
    imports: [
        CommonModule,
        ChipModule,
        ButtonModule,
        FormsModule,
        NgxTranslateModule,
        ReactiveFormsModule,
        DividerModule,
        PanelModule,
        InputTextModule,
        TableModule,
        AccordionModule,
        ConfirmDialogModule,
        ToastModule,
        TooltipModule,
        BadgeModule,
        AvatarModule,
    ],
    templateUrl: './vehicle-makes.component.html',
    styleUrl: './vehicle-makes.component.scss',
    providers: [ConfirmationService, MessageService],
})
export class VehicleMakesComponent {
    vehicleMakes!: CreateMakeResponse[];
    vehicleModels!: VehicleModelItem[];
    searchForm!: FormGroup;
    activePanelIndex: number | null = -1;
    showDialog!: boolean;
    messages: Message[] = [];

    languages: any[] = languages;
    selectedLanguage!: string;

    constructor(
        private makesService: VehicleMakesService,
        private fb: FormBuilder,
        private modelService: VehicleModelsService,
        private router: Router,
        private languageService: LanguageService,
        private translateService: TranslateService,
        private progressLoader: ProgressLoaderService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService
    ) { }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    ngOnInit() {
        this.initializeLocale();
        this.initializeSearchForm();
        this.fetchAllVehicleMakes();
        this.getActivePanelData();
    }

    getActivePanelData() {
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras.state) {
        }
    }

    confirmModelDeletion(
        index: number,
        modelId: string,
        modelName: string,
        makeId: string,
        makeName: string
    ) {
        this.translateService.get(['ARE_YOU_SURE_DELETE', 'MODEL_OF', 'VEHICLE_MAKE', 'CONFIRMATION', 'CANCEL', 'SAVE']).subscribe(res => {
            const cancel = res['CANCEL'];
            const save = res['SAVE'];

            this.confirmationService.confirm({
                message: `${res['ARE_YOU_SURE_DELETE']} ${res['MODEL_OF']} ${modelName} ${res['VEHICLE_MAKE']} ${makeName}?`,
                header: res['CONFIRMATION'],
                closeOnEscape: true,
                icon: 'pi pi-exclamation-triangle',
                rejectLabel: res['CANCEL'],
                rejectIcon: 'pi pi-times',
                rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
                acceptLabel: res['SAVE'],
                accept: () => {
                    this.removeVehicleModelFromDatabase(index, modelId, makeId);
                },
            });
        })
    }
    initializeSearchForm() {
        this.searchForm = this.fb.group({
            search: [''],
        });
    }

    deleteModel(
        i: number,
        modelId: string,
        modelName: string,
        makeId: string,
        makeName: string
    ) {
        this.confirmModelDeletion(i, modelId, modelName, makeId, makeName);
    }

    showSuccessMessage(message: string) {
        this.translateService.get(['Success', message]).subscribe(translations => {
            this.messageService.add({
                severity: 'success',
                summary: translations['Success'],
                life: 2000,
                detail: translations.get(message),
                key: 'tc',
            });
        })
    }

    showErrorMessage(error: string) {
        this.translateService.get(['Error', error]).subscribe(translations => {
            this.messageService.add({
                severity: 'error',
                summary: translations['Error'],
                life: 2000,
                detail: translations.get(error),
                key: 'tc',
            });
        })
    }

    fetchAllVehicleMakes() {
        this.progressLoader.show()
        this.makesService.getAllMakes().subscribe({
            next: (value) => {
                this.vehicleMakes = value;
                this.progressLoader.hide();
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                this.progressLoader.hide();
            },
        });
    }

    removeVehicleModelFromDatabase(
        index: number,
        modelId: string,
        makeId: string
    ) {
        this.progressLoader.show();
        this.modelService.deleteModel(makeId, modelId).subscribe({
            next: () => {
                this.fetchMakeModels(makeId, index);
                this.translateService.get('MODEL_DELETED_SUCCESS').subscribe(message => {
                    this.showSuccessMessage(message);
                });
                this.progressLoader.hide();
                this.activePanelIndex = index;
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                this.progressLoader.hide();
            },
        });
    }

    addVehicleModelClicked(id: string) {
        this.router.navigate(['/home/vehicle-models/new'], {
            queryParams: { makeId: id },
        });
    }

    searchMakeClicked() {
        const searchText = this.searchForm.value['search'];
        if (searchText.length !== 0) {
            this.vehicleMakes = this.vehicleMakes.filter((make) =>
                make.name
                    .toLowerCase()
                    .includes(this.searchForm.value['search'].toLowerCase())
            );
        } else {
            this.fetchAllVehicleMakes();
        }
    }

    addVehicleMakeClicked() {
        this.router.navigate(['/home/vehicle-makes/new']);
    }

    fetchMakeModels(makeId: string, index: number) {
        this.vehicleModels = [];
        this.modelService.getAllModels(makeId).subscribe({
            next: (value) => {
                this.vehicleModels = value;
            },
            error: (err) => {
                showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
            },
        });
        this.activePanelIndex = this.activePanelIndex === index ? null : index;
    }

    isPanelCollapsed(index: number): boolean {
        return this.activePanelIndex !== index;
    }
}
